import { EChartsExtensionInstallRegisters } from 'echarts/lib/extension';
import ExtensionAPI from 'echarts/lib/core/ExtensionAPI';
import { eachLineSeriesModel } from './helper';
import { DvLineSeriesModel } from './type';

/**
 * 折线图端点标签扩展功能
 * 
 * 功能说明：
 * 1. parseEndLabelZIndex: 动态调整端点标签的Z轴层级，解决标签重叠或遮挡问题
 *    - 通过 endLabel.dvZ 配置项控制标签的Z轴位置
 *    - 在图表更新时自动应用层级设置
 * 
 * 2. parseEndLabelRotate: 自动计算并设置端点标签的旋转角度
 *    - 当 dvRotate 设置为 'auto' 时，根据折线前两个点的斜率自动计算旋转角度
 *    - 使标签沿折线方向对齐，提升视觉效果
 * 
 * 使用方法：
 * - 在 series 配置中设置 endLabel.show: true 启用端点标签
 * - 通过 endLabel.dvZ 设置标签层级（数值越大层级越高）
 * - 通过 endLabel.dvRotate 设置旋转角度（'auto' 或具体角度值）
 */

/**
 * 设置端点标签的Z轴层级
 * @param model 折线图数据模型
 * @param chart ECharts扩展API实例
 */
function parseEndLabelZIndex(model: DvLineSeriesModel, chart: ExtensionAPI) {
  // 获取每个line模型对应的视图层
  const view = chart.getViewOfSeriesModel(model);
  // 获取折线对应的endLabel数据模型
  const isShowEndLabel = model.get(['endLabel', 'show']);
  // 获取配置项中的数据dvZ（Z轴层级）
  const dvEndLabelZ = model.get(['endLabel', 'dvZ']);

  if (!isShowEndLabel || !view._endLabel) {
    return;
  }

  // 从视图层修改endLabel的层级
  (view._endLabel as Element).attr({
    z: dvEndLabelZ
  });
}

/**
 * 自动计算并设置端点标签的旋转角度
 * @param model 折线图数据模型
 * @param chart ECharts扩展API实例
 */
function parseEndLabelRotate(model: DvLineSeriesModel, chart: ExtensionAPI) {
  const lineView = chart.getViewOfSeriesModel(model);
  const isEndLabelShow = model.get(['endLabel', 'show']);
  const dvRotate = model.get(['endLabel', 'dvRotate']);
  
  if (!isEndLabelShow) {
    return;
  }
  
  // 只有设置为 'auto' 时才自动计算旋转角度
  if (dvRotate !== 'auto') {
    return;
  }
  
  // 获取折线的前两个点来计算斜率
  const first = [lineView._points[0], lineView._points[1]];
  const second = [lineView._points[2], lineView._points[3]];
  // 计算旋转角度（弧度）
  const rotate2 = Math.atan((first[1] - second[1]) / (second[0] - first[0]));
  
  if (!lineView._endLabel) {
    return;
  }
  
  // 应用旋转角度到端点标签
  lineView._endLabel.attr({
    rotation: rotate2
  });
}

export function install(registers: EChartsExtensionInstallRegisters) {
  registers.registerUpdateLifecycle('series:afterupdate', (ecModel, api) => {
    eachLineSeriesModel(ecModel, model => {
      parseEndLabelZIndex(model, api);
      parseEndLabelRotate(model, api);
    });
  });
}
